import { useState, useEffect } from 'react';
import { getFeatureUsageAnalytics, getLearningInsightsMetrics, getKnowledgeMapMetrics } from '../../services/featureUsageService';
import BarChart from '../charts/BarChart';
import LineChart from '../charts/LineChart';

const FeatureUsageTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [featureData, setFeatureData] = useState<any>(null);
  const [insightsMetrics, setInsightsMetrics] = useState<any>(null);
  const [knowledgeMapMetrics, setKnowledgeMapMetrics] = useState<any>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [data, insights, knowledgeMap] = await Promise.all([
          getFeatureUsageAnalytics(30),
          getLearningInsightsMetrics(30).catch(() => null),
          getKnowledgeMapMetrics(30).catch(() => null),
        ]);
        setFeatureData(data);
        setInsightsMetrics(insights);
        setKnowledgeMapMetrics(knowledgeMap);
      } catch (error) {
        console.error('Error loading feature usage data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading feature usage analytics...</p>
      </div>
    );
  }

  if (!featureData) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>No feature usage data available</p>
      </div>
    );
  }

  // Feature adoption chart
  const adoptionData = {
    labels: featureData.featureAdoption.map((f: any) => f.featureName),
    datasets: [
      {
        label: 'Adoption Rate (%)',
        data: featureData.featureAdoption.map((f: any) => f.adoptionRate),
        backgroundColor: '#2563eb',
      },
    ],
  };

  // Feature usage frequency
  const usageData = {
    labels: featureData.featureAdoption.map((f: any) => f.featureName),
    datasets: [
      {
        label: 'Total Usage',
        data: featureData.featureAdoption.map((f: any) => f.totalUsage),
        backgroundColor: '#10b981',
      },
    ],
  };

  // Discovery time
  const discoveryData = {
    labels: featureData.featureAdoption.map((f: any) => f.featureName),
    datasets: [
      {
        label: 'Days to Discovery',
        data: featureData.featureAdoption.map((f: any) => f.discoveryTime),
        backgroundColor: '#f59e0b',
      },
    ],
  };

  // High-value user usage
  const highValueData = {
    labels: featureData.featureEffectiveness.map((f: any) => f.featureName),
    datasets: [
      {
        label: 'High-Value User Usage (%)',
        data: featureData.featureEffectiveness.map((f: any) => f.highValueUserUsage),
        backgroundColor: '#8b5cf6',
      },
    ],
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ marginBottom: '2rem', fontSize: '1.75rem', fontWeight: 'bold' }}>
        Feature Usage Analytics
      </h1>

      {/* Charts */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Feature Adoption Rates</h3>
          <BarChart data={adoptionData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Feature Usage Frequency</h3>
          <BarChart data={usageData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Average Discovery Time</h3>
          <BarChart data={discoveryData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>High-Value User Feature Usage</h3>
          <BarChart data={highValueData} height={300} />
        </div>
      </div>

      {/* Feature Details Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden',
      }}>
        <h3 style={{ padding: '1rem', margin: 0, borderBottom: '1px solid #e0e0e0' }}>
          Feature Adoption Details
        </h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Feature</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Adoption Rate</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Unique Users</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Total Usage</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Discovery Time (days)</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>High-Value Usage</th>
            </tr>
          </thead>
          <tbody>
            {featureData.featureAdoption.map((feature: any, index: number) => {
              const effectiveness = featureData.featureEffectiveness.find(
                (e: any) => e.featureName === feature.featureName
              );
              return (
                <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '1rem' }}>{feature.featureName}</td>
                  <td style={{ padding: '1rem', color: feature.adoptionRate > 50 ? '#10b981' : '#ef4444' }}>
                    {feature.adoptionRate.toFixed(1)}%
                  </td>
                  <td style={{ padding: '1rem' }}>{feature.uniqueUsers}</td>
                  <td style={{ padding: '1rem' }}>{feature.totalUsage}</td>
                  <td style={{ padding: '1rem' }}>{feature.discoveryTime.toFixed(1)}</td>
                  <td style={{ padding: '1rem' }}>
                    {effectiveness ? effectiveness.highValueUserUsage.toFixed(1) : '0'}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Learning Insights & Knowledge Map Detailed Metrics */}
      {(insightsMetrics || knowledgeMapMetrics) && (
        <div style={{ marginTop: '2rem' }}>
          <h2 style={{ marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: 'bold' }}>
            New Features - Detailed Metrics
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
            gap: '1.5rem',
          }}>
            {/* Learning Insights Card */}
            {insightsMetrics && (
              <div style={{
                backgroundColor: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}>
                <h3 style={{ marginTop: 0, color: '#2563eb' }}>📊 Learning Insights</h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '1rem',
                  marginTop: '1rem',
                }}>
                  <div>
                    <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Total Views</p>
                    <h4 style={{ margin: '0.25rem 0', fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {insightsMetrics.totalViews}
                    </h4>
                  </div>
                  <div>
                    <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Unique Users</p>
                    <h4 style={{ margin: '0.25rem 0', fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {insightsMetrics.uniqueUsers}
                    </h4>
                  </div>
                  <div>
                    <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Total Loads</p>
                    <h4 style={{ margin: '0.25rem 0', fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {insightsMetrics.totalLoads}
                    </h4>
                  </div>
                  <div>
                    <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Navigations to Paths</p>
                    <h4 style={{ margin: '0.25rem 0', fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {insightsMetrics.totalNavigations}
                    </h4>
                  </div>
                  <div>
                    <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Avg Time Spent</p>
                    <h4 style={{ margin: '0.25rem 0', fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {Math.round(insightsMetrics.averageTimeSpent)}s
                    </h4>
                  </div>
                  <div>
                    <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Refreshes</p>
                    <h4 style={{ margin: '0.25rem 0', fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {insightsMetrics.totalRefreshes}
                    </h4>
                  </div>
                </div>
                {Object.keys(insightsMetrics.timePeriodDistribution).length > 0 && (
                  <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e0e0e0' }}>
                    <p style={{ margin: 0, color: '#666', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                      Time Period Preferences:
                    </p>
                    {Object.entries(insightsMetrics.timePeriodDistribution).map(([period, count]) => (
                      <div key={period} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                        <span>{period}:</span>
                        <span style={{ fontWeight: 'bold' }}>{count as number}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Knowledge Map Card */}
            {knowledgeMapMetrics && (
              <div style={{
                backgroundColor: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}>
                <h3 style={{ marginTop: 0, color: '#10b981' }}>🗺️ Knowledge Map</h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '1rem',
                  marginTop: '1rem',
                }}>
                  <div>
                    <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Total Views</p>
                    <h4 style={{ margin: '0.25rem 0', fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {knowledgeMapMetrics.totalViews}
                    </h4>
                  </div>
                  <div>
                    <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Unique Users</p>
                    <h4 style={{ margin: '0.25rem 0', fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {knowledgeMapMetrics.uniqueUsers}
                    </h4>
                  </div>
                  <div>
                    <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Total Loads</p>
                    <h4 style={{ margin: '0.25rem 0', fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {knowledgeMapMetrics.totalLoads}
                    </h4>
                  </div>
                  <div>
                    <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Node Taps</p>
                    <h4 style={{ margin: '0.25rem 0', fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {knowledgeMapMetrics.totalNodeTaps}
                    </h4>
                  </div>
                  <div>
                    <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Navigations to Paths</p>
                    <h4 style={{ margin: '0.25rem 0', fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {knowledgeMapMetrics.totalNavigations}
                    </h4>
                  </div>
                  <div>
                    <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Avg Time Spent</p>
                    <h4 style={{ margin: '0.25rem 0', fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {Math.round(knowledgeMapMetrics.averageTimeSpent)}s
                    </h4>
                  </div>
                </div>
                {Object.keys(knowledgeMapMetrics.nodeStatusDistribution).length > 0 && (
                  <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e0e0e0' }}>
                    <p style={{ margin: 0, color: '#666', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                      Node Status Distribution:
                    </p>
                    {Object.entries(knowledgeMapMetrics.nodeStatusDistribution).map(([status, count]) => (
                      <div key={status} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                        <span>{status}:</span>
                        <span style={{ fontWeight: 'bold' }}>{count as number}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FeatureUsageTab;

